import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ThemedToaster from '@/components/ThemedToaster';
import '@/App.css';
import '@/styles/bz.css';
import '@/styles/vivid.css';
import '@/styles/live.css';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LangProvider } from '@/contexts/LangContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { NavBadgesProvider } from '@/contexts/NavBadgesContext';
import { BusinessAuthProvider, useBusinessAuth } from '@/contexts/BusinessAuthContext';
import AppLayout from '@/components/AppLayout';
import LoadingScreen from '@/components/LoadingScreen';
import RequireCapability from '@/components/business/RequireCapability';
import useAppVersionWatcher from '@/hooks/useAppVersionWatcher';

// Eagerly imported — these are on the critical path (initial paint after auth resolve).
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Landing from '@/pages/Landing';

// ---- Consumer (legacy) lazy routes (kept accessible at their original paths)
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Feed = lazy(() => import('@/pages/Feed'));
const FeedByUser = lazy(() => import('@/pages/FeedByUser'));
const Coach = lazy(() => import('@/pages/Coach'));
const Profile = lazy(() => import('@/pages/Profile'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const Admin = lazy(() => import('@/pages/Admin'));
const Goals = lazy(() => import('@/pages/Goals'));
const GoalDetail = lazy(() => import('@/pages/GoalDetail'));
const Friends = lazy(() => import('@/pages/Friends'));
const UserProfile = lazy(() => import('@/pages/UserProfile'));
const Achievements = lazy(() => import('@/pages/Achievements'));
const SetupUsername = lazy(() => import('@/pages/SetupUsername'));
const Invite = lazy(() => import('@/pages/Invite'));
const ChallengeBoard = lazy(() => import('@/pages/ChallengeBoard'));
const JoinHandle = lazy(() => import('@/pages/JoinHandle'));
const DMList = lazy(() => import('@/pages/DMList'));
const DMChat = lazy(() => import('@/pages/DMChat'));
const Groups = lazy(() => import('@/pages/Groups'));
const GroupCreate = lazy(() => import('@/pages/GroupCreate'));
const GroupDetail = lazy(() => import('@/pages/GroupDetail'));
const GroupJoin = lazy(() => import('@/pages/GroupJoin'));
const PostDetail = lazy(() => import('@/pages/PostDetail'));
const Terms = lazy(() => import('@/pages/Terms'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Redeem = lazy(() => import('@/pages/Redeem'));
const FutureMe = lazy(() => import('@/pages/FutureMe'));
const Marketplace = lazy(() => import('@/pages/Marketplace'));
const MarketplaceCategory = lazy(() => import('@/pages/MarketplaceCategory'));
const MarketplaceSaved = lazy(() => import('@/pages/MarketplaceSaved'));

// ---- BecAIm Business (B2B SaaS) lazy routes
const BusinessLanding = lazy(() => import('@/pages/business/BusinessLanding'));
const BusinessPricing = lazy(() => import('@/pages/business/BusinessPricing'));
const BusinessUseCases = lazy(() => import('@/pages/business/BusinessUseCases'));
const BusinessDemo = lazy(() => import('@/pages/business/BusinessDemo'));
const BusinessSignup = lazy(() => import('@/pages/business/BusinessSignup'));
const BusinessPrivacy = lazy(() => import('@/pages/business/BusinessPrivacy'));
const BusinessTerms = lazy(() => import('@/pages/business/BusinessTerms'));
const BusinessLogin = lazy(() => import('@/pages/business/BusinessLogin'));
const BusinessAcceptInvite = lazy(() => import('@/pages/business/BusinessAcceptInvite'));
const BusinessForgotPassword = lazy(() => import('@/pages/business/BusinessForgotPassword'));
const BusinessResetPassword = lazy(() => import('@/pages/business/BusinessResetPassword'));
const BusinessLayout = lazy(() => import('@/components/business/BusinessLayout'));
const BusinessDashboard = lazy(() => import('@/pages/business/BusinessDashboard'));
const BusinessStart = lazy(() => import('@/pages/business/BusinessStart'));
const BusinessExplore = lazy(() => import('@/pages/business/BusinessExplore'));
const BusinessDesignLab = lazy(() => import('@/pages/business/BusinessDesignLab'));
const BusinessTeam = lazy(() => import('@/pages/business/BusinessTeam'));
const BusinessGoals = lazy(() => import('@/pages/business/BusinessGoals'));
const BusinessGoalDetail = lazy(() => import('@/pages/business/BusinessGoalDetail'));
const BusinessMissions = lazy(() => import('@/pages/business/BusinessMissions'));
const BusinessAnalytics = lazy(() => import('@/pages/business/BusinessAnalytics'));
const BusinessCoach = lazy(() => import('@/pages/business/BusinessCoach'));
const BusinessActivity = lazy(() => import('@/pages/business/BusinessActivity'));
const BusinessPerformance = lazy(() => import('@/pages/business/BusinessPerformance'));
const BusinessUserPerformance = lazy(() => import('@/pages/business/BusinessUserPerformance'));
const BusinessMarketplace = lazy(() => import('@/pages/business/BusinessMarketplace'));
const BusinessParkingLot = lazy(() => import('@/pages/business/BusinessParkingLot'));
const BusinessHelp = lazy(() => import('@/pages/business/BusinessHelp'));
const BusinessSettings = lazy(() => import('@/pages/business/BusinessSettings'));
const BusinessSoon = lazy(() => import('@/pages/business/BusinessSoon'));
// BeCaim 2.0 — new experiences
const GoalComposer = lazy(() => import('@/pages/business/GoalComposer'));
const MissionWorkspace = lazy(() => import('@/pages/business/MissionWorkspace'));
const BusinessIntelligence = lazy(() => import('@/pages/business/BusinessIntelligence'));
const BusinessInbox = lazy(() => import('@/pages/business/BusinessInbox'));
const BusinessKnowledge = lazy(() => import('@/pages/business/BusinessKnowledge'));
const SuperAdminLayout = lazy(() => import('@/components/business/SuperAdminLayout'));
const SuperAdminDashboard = lazy(() => import('@/pages/business/SuperAdminDashboard'));
const SuperAdminTenants = lazy(() => import('@/pages/business/SuperAdminTenants'));
const SuperAdminSubscriptions = lazy(() => import('@/pages/business/SuperAdminSubscriptions'));
const SuperAdminAICost = lazy(() => import('@/pages/business/SuperAdminAICost'));
const SuperAdminProcurementCosts = lazy(() => import('@/pages/business/SuperAdminProcurementCosts'));
const SuperAdminCredits = lazy(() => import('@/pages/business/SuperAdminCredits'));
const SuperAdminUsage = lazy(() => import('@/pages/business/SuperAdminUsage'));
const SuperAdminLeads = lazy(() => import('@/pages/business/SuperAdminLeads'));
const SuperAdminWebsite = lazy(() => import('@/pages/business/SuperAdminWebsite'));
const SuperAdminAIProvider = lazy(() => import('@/pages/business/SuperAdminAIProvider'));

// Procurement Copilot — isolated module.
const ProcurementLayout = lazy(() => import('@/pages/procurement/ProcurementLayout'));
// Iter 141 (Feb 2026) — Sales Co-Pilot
const SalesLayout = lazy(() => import('@/pages/business/sales/SalesLayout'));
const SalesDashboard = lazy(() => import('@/pages/business/sales/SalesDashboard'));
const SalesLeadSearch = lazy(() => import('@/pages/business/sales/SalesLeadSearch'));
const SalesCampaigns = lazy(() => import('@/pages/business/sales/SalesCampaigns'));
const SalesLeads = lazy(() => import('@/pages/business/sales/SalesLeads'));
const SalesTemplates = lazy(() => import('@/pages/business/sales/SalesTemplates'));
const SalesWebAnalytics = lazy(() => import('@/pages/business/sales/SalesWebAnalytics'));
const WebAnalyticsTracker = lazy(() => import('@/lib/WebAnalyticsTracker'));
const SalesCampaignDetail = lazy(() => import('@/pages/business/sales/SalesCampaignDetail'));
const SalesInbox = lazy(() => import('@/pages/business/sales/SalesInbox'));
const ProcurementDashboard = lazy(() => import('@/pages/procurement/ProcurementDashboard'));
const ProcurementRequirements = lazy(() => import('@/pages/procurement/ProcurementRequirements'));
const ProcurementSearch = lazy(() => import('@/pages/procurement/ProcurementSearch'));
const ProcurementSearchDetail = lazy(() => import('@/pages/procurement/ProcurementSearchDetail'));
const ProcurementSuppliers = lazy(() => import('@/pages/procurement/ProcurementSuppliers'));
const SupplierDetail = lazy(() => import('@/pages/procurement/SupplierDetail'));
const ProcurementRFQs = lazy(() => import('@/pages/procurement/ProcurementRFQs'));
const ProcurementRFQDetail = lazy(() => import('@/pages/procurement/ProcurementRFQDetail'));
const ProcurementCommunications = lazy(() => import('@/pages/procurement/ProcurementCommunications'));
const ProcurementFollowUps = lazy(() => import('@/pages/procurement/ProcurementFollowUps'));
const ProcurementQuotations = lazy(() => import('@/pages/procurement/ProcurementQuotations'));
const ProcurementComparison = lazy(() => import('@/pages/procurement/ProcurementComparison'));
const ProcurementInbox = lazy(() => import('@/pages/procurement/ProcurementInbox'));
const TrustedSuppliers = lazy(() => import('@/pages/procurement/TrustedSuppliers'));
const TrustedSupplierCategory = lazy(() => import('@/pages/procurement/TrustedSuppliers').then(m => ({ default: m.TrustedSupplierCategory })));
const ProcurementProducts = lazy(() => import('@/pages/procurement/ProcurementProducts'));
const ProcurementBulkProgram = lazy(() => import('@/pages/procurement/ProcurementBulkProgram'));
const ProcurementProductDetail = lazy(() => import('@/pages/procurement/ProcurementProductDetail'));
const ProcurementRFQAudit = lazy(() => import('@/pages/procurement/ProcurementRFQAudit'));
const ProcurementTemplates = lazy(() => import('@/pages/procurement/ProcurementTemplates'));

function Private({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin' && (!user.username || !user.display_name)) return <Navigate to="/setup-username" replace />;
  if (!user.onboarded && user.role !== 'super_admin') return <Navigate to="/onboarding" replace />;
  return children;
}

// Business private gate — checks the SEPARATE business auth token.
function BusinessPrivate({ children }) {
  const { user, loading } = useBusinessAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/business/login" replace />;
  return children;
}

// Root → BecAIm Business landing. This workspace IS the Business app.
// (Consumer routes remain accessible if visited directly, e.g. /login, /app.)
function Root() {
  return <BusinessLanding />;
}

function App() {
  useAppVersionWatcher();
  return (
    <ThemeProvider>
      <LangProvider>
      <AuthProvider>
      <BusinessAuthProvider>
        <BrowserRouter>
          <NavBadgesProvider>
          <ThemedToaster richColors position="top-center" />
          <Suspense fallback={<LoadingScreen />}>
            {/* iter 234 — public-page visitor beacon (marketing paths only) */}
            <WebAnalyticsTracker />
            <Routes>
              <Route path="/" element={<Root />} />

              {/* ---- BecAIm Business (primary) ---- */}
              <Route path="/business" element={<BusinessLanding />} />
              <Route path="/business/explore/:id" element={<BusinessExplore />} />
              <Route path="/business/design-lab" element={<BusinessDesignLab />} />
              <Route path="/business/pricing" element={<BusinessPricing />} />
              <Route path="/business/use-cases" element={<BusinessUseCases />} />
              <Route path="/business/demo" element={<BusinessDemo />} />
              <Route path="/business/privacy" element={<BusinessPrivacy />} />
              <Route path="/business/terms" element={<BusinessTerms />} />
              {/* Public SEO leaderboard — powered by the anonymised KG. No auth. */}
              <Route path="/trusted-suppliers" element={<TrustedSuppliers />} />
              <Route path="/trusted-suppliers/:slug" element={<TrustedSupplierCategory />} />
              <Route path="/business/signup" element={<BusinessSignup />} />
              <Route path="/business/login" element={<BusinessLogin />} />
              <Route path="/business/forgot-password" element={<BusinessForgotPassword />} />
              <Route path="/business/reset-password" element={<BusinessResetPassword />} />
              <Route path="/business/accept-invite" element={<BusinessAcceptInvite />} />
              <Route element={<BusinessPrivate><BusinessLayout /></BusinessPrivate>}>
                <Route path="/business/dashboard" element={<BusinessDashboard />} />
                <Route path="/business/start" element={<BusinessStart />} />
                <Route path="/business/team" element={<BusinessTeam />} />
                <Route path="/business/goals" element={<BusinessGoals />} />
                <Route path="/business/goals/new" element={<GoalComposer />} />
                <Route path="/business/goals/:id" element={<BusinessGoalDetail />} />
                <Route path="/business/missions" element={<BusinessMissions />} />
                <Route path="/business/missions/:id" element={<MissionWorkspace />} />
                <Route path="/business/intelligence" element={<BusinessIntelligence />} />
                <Route path="/business/inbox" element={<BusinessInbox />} />
                <Route path="/business/knowledge" element={<BusinessKnowledge />} />
                <Route path="/business/analytics" element={<BusinessAnalytics />} />
                <Route path="/business/coach" element={<BusinessCoach />} />
                <Route path="/business/activity" element={<BusinessActivity />} />
                <Route path="/business/performance" element={<BusinessPerformance />} />
                <Route path="/business/performance/:userId" element={<BusinessUserPerformance />} />
                <Route path="/business/marketplace" element={<BusinessMarketplace />} />
                <Route path="/business/parking-lot" element={<BusinessParkingLot />} />
                <Route path="/business/help" element={<BusinessHelp />} />
                <Route path="/business/settings" element={<BusinessSettings />} />

              {/* ---- Sales Co-Pilot: a section inside the shell (same routes) ---- */}
              <Route element={<RequireCapability capability="sales"><SalesLayout /></RequireCapability>}>
                <Route path="/business/sales" element={<SalesDashboard />} />
                <Route path="/business/sales/search" element={<SalesLeadSearch />} />
                <Route path="/business/sales/campaigns" element={<SalesCampaigns />} />
                <Route path="/business/sales/campaigns/new" element={<Navigate to="/business/sales/search" replace />} />
                <Route path="/business/sales/campaigns/:id" element={<SalesCampaignDetail />} />
                <Route path="/business/sales/leads" element={<SalesLeads />} />
                <Route path="/business/sales/templates" element={<SalesTemplates />} />
                <Route path="/business/sales/inbox" element={<SalesInbox />} />
                <Route path="/business/sales/web-analytics" element={<SalesWebAnalytics />} />
              </Route>

              {/* ---- Procurement Copilot: a section inside the shell (same routes) ---- */}
              <Route element={<RequireCapability capability="procurement"><ProcurementLayout /></RequireCapability>}>
                <Route path="/procurement" element={<Navigate to="/procurement/dashboard" replace />} />
                <Route path="/procurement/dashboard" element={<ProcurementDashboard />} />
                <Route path="/procurement/requirements" element={<ProcurementRequirements />} />
                <Route path="/procurement/search" element={<ProcurementSearch />} />
                <Route path="/procurement/search/:id" element={<ProcurementSearchDetail />} />
                <Route path="/procurement/products" element={<ProcurementProducts />} />
                <Route path="/procurement/products/:id" element={<ProcurementProductDetail />} />
                <Route path="/procurement/products/:pid/program" element={<ProcurementBulkProgram />} />
                <Route path="/procurement/rfq-audit" element={<ProcurementRFQAudit />} />
                <Route path="/procurement/templates" element={<ProcurementTemplates />} />
                <Route path="/procurement/suppliers" element={<ProcurementSuppliers />} />
                <Route path="/procurement/suppliers/:id" element={<SupplierDetail />} />
                <Route path="/procurement/rfqs" element={<ProcurementRFQs />} />
                <Route path="/procurement/rfqs/:id" element={<ProcurementRFQDetail />} />
                <Route path="/procurement/communications" element={<ProcurementCommunications />} />
                <Route path="/procurement/follow-ups" element={<ProcurementFollowUps />} />
                <Route path="/procurement/inbox" element={<ProcurementInbox />} />
                <Route path="/procurement/quotations" element={<ProcurementQuotations />} />
                <Route path="/procurement/landed-cost" element={<Navigate to="/procurement/comparison" replace />} />
                <Route path="/procurement/comparison" element={<ProcurementComparison />} />
                <Route path="/procurement/recommendations" element={<Navigate to="/procurement/comparison" replace />} />
              </Route>
              </Route>

              {/* ---- Super Admin (platform owner) ---- */}
              <Route element={<BusinessPrivate><SuperAdminLayout /></BusinessPrivate>}>
                <Route path="/super-admin" element={<Navigate to="/super-admin/dashboard" replace />} />
                <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
                <Route path="/super-admin/tenants" element={<SuperAdminTenants />} />
                <Route path="/super-admin/ai-cost" element={<SuperAdminAICost />} />
                <Route path="/super-admin/procurement-costs" element={<SuperAdminProcurementCosts />} />
                <Route path="/super-admin/credits" element={<SuperAdminCredits />} />
                <Route path="/super-admin/usage" element={<SuperAdminUsage />} />
                <Route path="/super-admin/subscriptions" element={<SuperAdminSubscriptions />} />
                <Route path="/super-admin/leads" element={<SuperAdminLeads />} />
                <Route path="/super-admin/website" element={<SuperAdminWebsite />} />
                <Route path="/super-admin/ai-provider" element={<SuperAdminAIProvider />} />
              </Route>

              {/* ---- Consumer (legacy) routes ---- */}
              <Route path="/login" element={<Login />} />
              <Route path="/legacy" element={<Landing />} />
              <Route path="/join" element={<Login />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/setup-username" element={<Private><SetupUsername /></Private>} />
              <Route path="/onboarding" element={<Private><Onboarding /></Private>} />
              <Route path="/notifications" element={<Private><Notifications /></Private>} />
              <Route path="/redeem" element={<Private><Redeem /></Private>} />
              <Route path="/future-me" element={<Private><FutureMe /></Private>} />
              <Route path="/marketplace" element={<Private><Marketplace /></Private>} />
              <Route path="/marketplace/saved" element={<Private><MarketplaceSaved /></Private>} />
              <Route path="/marketplace/c/:slug" element={<Private><MarketplaceCategory /></Private>} />
              <Route path="/goals" element={<Private><Goals /></Private>} />
              <Route path="/goals/:id" element={<Private><GoalDetail /></Private>} />
              <Route path="/achievements" element={<Private><Achievements /></Private>} />
              <Route path="/invite" element={<Private><Invite /></Private>} />
              <Route path="/challenges" element={<Private><ChallengeBoard /></Private>} />
              <Route path="/admin" element={<Private><Admin /></Private>} />
              <Route path="/groups/join/:code" element={<Private><GroupJoin /></Private>} />

              <Route element={<RoleGate><AppLayout /></RoleGate>}>
                <Route path="/app" element={<Dashboard />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/feed/by-user/:author_id" element={<FeedByUser />} />
                <Route path="/feed/post/:post_id" element={<PostDetail />} />
                <Route path="/dm" element={<DMList />} />
                <Route path="/dm/:user_id" element={<DMChat />} />
                <Route path="/friends" element={<Friends />} />
                <Route path="/coach" element={<Coach />} />
                <Route path="/groups" element={<Groups />} />
                <Route path="/groups/new" element={<GroupCreate />} />
                <Route path="/groups/:id" element={<GroupDetail />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/users/:id" element={<UserProfile />} />
              </Route>
              <Route path="*" element={<JoinHandle />} />
            </Routes>
          </Suspense>
          </NavBadgesProvider>
        </BrowserRouter>
      </BusinessAuthProvider>
      </AuthProvider>
    </LangProvider>
    </ThemeProvider>
  );
}

export default App;
